import React, { useRef, useEffect } from 'react';

interface Bowler {
    FICHANAL: number;
    NOMBRECOMPLETO: string;
    EMAIL: string;
    TELEFONO: string | null;
    CITY: string | null;
    CVEESTADO: string | null;
    DIVISION_NOMBRE: string | null;
    RECRUITER_NAME: string | null;
}

interface Package {
    DESCRIPCION: string;
    COSTO: number;
    PACKAGETYPE: string;
}

interface Balance {
    COMPRADO: number;
    PAGADO: number;
    BALANCE: number;
}

interface Payment {
    FECHA: string;
    CANTIDAD: number;
    CVEFORMAPAGO: number;
}

interface RegisteredSquad {
    TURNO: number;
    DIASEMANA_I: string;
    HORARIO_I: string;
    CVEEVENTO: number;
    EVENTO_NOMBRE: string;
}

interface SchedulePrintViewProps {
    bowler: Bowler;
    packages: Package[];
    payments: Payment[];
    balance: Balance;
    registeredSquads: RegisteredSquad[];
    onClose?: () => void;
}

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const payTypeLabel = (id: number) =>
    ({ 1: 'Cash', 2: 'Check', 3: 'Card' } as Record<number, string>)[id] ?? `Type ${id}`;
const pkgTypeLabel = (t: string) =>
    ({ '1': 'Main', '2': 'Re-Entry', '3': 'Side Event' } as Record<string, string>)[t] ?? 'Package';

const SchedulePrintView: React.FC<SchedulePrintViewProps> = ({
    bowler, packages, payments, balance, registeredSquads, onClose,
}) => {
    const eventGroups: Record<string, RegisteredSquad[]> = {};
    registeredSquads.forEach(sq => {
        if (!eventGroups[sq.EVENTO_NOMBRE]) eventGroups[sq.EVENTO_NOMBRE] = [];
        eventGroups[sq.EVENTO_NOMBRE].push(sq);
    });

    const balanceColor = Number(balance.BALANCE) > 0 ? '#dc2626'
        : Number(balance.BALANCE) < 0 ? '#2563eb' : '#374151';

    const pkgRows = packages.map(p =>
        `<tr>
            <td style="color:#4338ca;font-weight:600">${pkgTypeLabel(p.PACKAGETYPE)}</td>
            <td>${p.DESCRIPCION}</td>
            <td class="right">${fmt(Number(p.COSTO))}</td>
        </tr>`
    ).join('');

    const payRows = payments.map(p =>
        `<tr>
            <td>${payTypeLabel(p.CVEFORMAPAGO)}</td>
            <td>${p.FECHA ? new Date(p.FECHA).toLocaleDateString() : '\u2014'}</td>
            <td class="right" style="color:#16a34a">${fmt(Number(p.CANTIDAD))}</td>
        </tr>`
    ).join('');

    const scheduleHtml = Object.entries(eventGroups).map(([eventName, squads]) =>
        `<div class="event-block">
            <div class="event-title">${eventName}</div>
            <table>
                <thead><tr><th>Squad #</th><th>Day</th><th>Time</th></tr></thead>
                <tbody>
                    ${squads.map(sq =>
                        `<tr><td>${sq.TURNO}</td><td>${sq.DIASEMANA_I}</td><td>${sq.HORARIO_I}</td></tr>`
                    ).join('')}
                </tbody>
            </table>
        </div>`
    ).join('');

    const printContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Schedule \u2014 ${bowler.NOMBRECOMPLETO}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 28px; background: #fff; }
    .header { display: flex; align-items: flex-start; justify-content: space-between;
              border-bottom: 3px solid #4f46e5; padding-bottom: 14px; margin-bottom: 20px; }
    .bowler-name { font-size: 22px; font-weight: bold; color: #1e1b4b; }
    .bowler-sub  { font-size: 12px; color: #6b7280; margin-top: 3px; }
    .logo        { height: 48px; width: auto; }
    .two-col { display: flex; gap: 16px; margin-bottom: 16px; }
    .card    { flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
    .card-title { font-size: 9px; font-weight: bold; color: #9ca3af;
                  text-transform: uppercase; letter-spacing: .06em; margin-bottom: 8px; }
    table   { width: 100%; border-collapse: collapse; }
    th      { background: #eef2ff; color: #4338ca; font-size: 10px;
              padding: 5px 8px; text-align: left; border: 1px solid #ddd; }
    td      { padding: 5px 8px; border: 1px solid #e5e7eb; font-size: 11px; vertical-align: middle; }
    .right  { text-align: right; }
    tr.total td { font-weight: bold; background: #f3f4f6; }
    .schedule-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
    .event-block  { margin-bottom: 12px; }
    .event-block:last-child { margin-bottom: 0; }
    .event-title  { font-size: 13px; font-weight: bold; color: #4338ca;
                    padding: 4px 10px; background: #eef2ff; border-radius: 4px; margin-bottom: 5px; }
    .footer { margin-top: 18px; text-align: center; font-size: 10px;
              color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 8px; }
    @media print { body { padding: 14px; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="bowler-name">${bowler.NOMBRECOMPLETO}</div>
      <div class="bowler-sub">TAT #${bowler.FICHANAL} &nbsp;|&nbsp; ${bowler.EMAIL}${bowler.TELEFONO ? ` &nbsp;|&nbsp; ${bowler.TELEFONO}` : ''}${bowler.CITY ? ` &nbsp;|&nbsp; ${bowler.CITY}${bowler.CVEESTADO ? `, ${bowler.CVEESTADO}` : ''}` : ''}</div>
      ${bowler.DIVISION_NOMBRE ? `<div class="bowler-sub">Division: <strong>${bowler.DIVISION_NOMBRE}</strong></div>` : ''}
      ${bowler.RECRUITER_NAME  ? `<div class="bowler-sub">Recruiter: <strong>${bowler.RECRUITER_NAME}</strong></div>` : ''}
    </div>
    <img src="${window.location.origin}/logo.avif" alt="TAT" class="logo" onerror="this.style.display='none'" />
  </div>
  <div class="two-col">
    <div class="card">
      <div class="card-title">Packages Purchased</div>
      ${packages.length === 0
        ? '<p style="color:#9ca3af;font-style:italic;font-size:11px">No packages</p>'
        : `<table>
             <thead><tr><th>Type</th><th>Description</th><th class="right">Cost</th></tr></thead>
             <tbody>
               ${pkgRows}
               <tr class="total">
                 <td colspan="2" class="right">Total</td>
                 <td class="right">${fmt(packages.reduce((s, p) => s + Number(p.COSTO), 0))}</td>
               </tr>
             </tbody>
           </table>`}
    </div>
    <div class="card">
      <div class="card-title">Balance</div>
      <table>
        <tbody>
          <tr><td>Total Bought</td><td class="right">${fmt(Number(balance.COMPRADO))}</td></tr>
          <tr><td>Total Paid</td>  <td class="right" style="color:#16a34a">${fmt(Number(balance.PAGADO))}</td></tr>
          <tr class="total">
            <td>Balance Due</td>
            <td class="right" style="color:${balanceColor};font-size:14px">${fmt(Number(balance.BALANCE))}</td>
          </tr>
        </tbody>
      </table>
      ${payments.length > 0 ? `
        <div class="card-title" style="margin-top:12px">Payment History</div>
        <table>
          <thead><tr><th>Method</th><th>Date</th><th class="right">Amount</th></tr></thead>
          <tbody>${payRows}</tbody>
        </table>` : ''}
    </div>
  </div>
  <div class="schedule-card">
    <div class="card-title">Tournament Schedule</div>
    ${registeredSquads.length === 0
      ? '<p style="color:#9ca3af;font-style:italic;font-size:11px">No squads scheduled yet.</p>'
      : scheduleHtml}
  </div>
  <div class="footer">
    Printed on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()} &nbsp;|&nbsp; TAT Bowling
  </div>
</body>
</html>`;

    const hasPrinted = useRef(false);

    useEffect(() => {
        if (hasPrinted.current) return;
        hasPrinted.current = true;
        const w = window.open('', '_blank');
        if (w) {
            w.document.write(printContent);
            w.document.close();
            w.focus();
            w.onbeforeunload = () => { if (typeof onClose === 'function') onClose(); };
            w.print();
        }
    }, []);

    return null;
};

export default SchedulePrintView;
